/**
 * A currency per location (QA4-D6, 2026-10-18).
 *
 * Every price is integer minor units, and until this decision one
 * `clinics.currency` supplied the exponent for all of them. Now a location has
 * its own, so two things the product never needed become rules:
 *
 *   * **converting a location's prices at a typed rate**, rounded to the
 *     nearest minor unit, half up — and across two exponents, because 1 USD in
 *     cents is not 1 LBP in anything;
 *   * **never adding two currencies together.** Where amounts from several
 *     locations meet — a patient's balance, Home's money to collect — they are
 *     kept per currency, and every reader shows each one.
 *
 * Both live here because the API does the first and both sides read the
 * second, and one rule implemented twice eventually disagrees about money.
 */

/**
 * How many decimal places a currency has, from `Intl` rather than an assumption
 * (QA #69).
 *
 * **Money is stored as integer minor units, and "minor unit" is not always a
 * hundredth.** Yen and the Lebanese pound have none at all; the dinars have
 * three. A hardcoded hundred is therefore not a simplification but a
 * hundredfold error, in whichever direction the currency differs — and it is an
 * error nobody catches on a USD demo clinic.
 *
 * This lives in the shared package because both sides convert money and they
 * must agree: the browser parses what an admin types into a price box, the API
 * parses what they import as a CSV, and the same catalogue is read back through
 * both. Two implementations of this rule would eventually disagree about the
 * same price list.
 *
 * **An unknown code falls back to two**, which is what `Intl` itself does. That
 * is a guess, but it is the guess every other system makes, and the CSV import
 * refuses more decimals than the exponent allows rather than rounding silently.
 */
export function minorUnitExponent(currency: string): number {
  try {
    return (
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** How many minor units one unit of this currency has. `USD` → 100, `JPY` → 1. */
export function minorUnitsPerUnit(currency: string): number {
  return 10 ** minorUnitExponent(currency);
}

/** An exact positive rate, `numerator / denominator`, as typed. */
export interface ExchangeRate {
  numerator: bigint;
  denominator: bigint;
}

/** The longest rate accepted, digits either side of the point. */
const RATE = /^(\d{1,12})(?:\.(\d{1,12}))?$/;

/**
 * What an admin typed as "1 old = X new", or null if it is not a positive
 * number. **Exact, not a float**: 0.1 × 3 is not 0.3 in binary, and a rate is
 * multiplied into every price at a location, so a binary rounding error is a
 * price list wrong by one minor unit in places nobody looks.
 */
export function parseExchangeRate(typed: string): ExchangeRate | null {
  const match = RATE.exec(typed.trim().replace(/,/g, ""));
  if (match === null) return null;
  const decimals = match[2] ?? "";
  const numerator = BigInt(`${match[1] ?? "0"}${decimals}`);
  if (numerator === 0n) return null;
  return { numerator, denominator: 10n ** BigInt(decimals.length) };
}

/**
 * Minor units of `from`, as minor units of `to`, at `rate` (1 `from` = rate
 * `to`). **Rounded to the nearest minor unit, half up** (QA4-D6) — the result
 * is the exact product rounded once, so a price of 0.005 after conversion
 * becomes 0.01 and never drifts with the order of the arithmetic.
 *
 * The exponents differ between currencies and are accounted for here: 1000
 * cents of USD at 89 500 is 89 500 000 LBP, not 895 000.
 */
export function convertMinorUnits(
  cents: number,
  from: string,
  to: string,
  rate: ExchangeRate,
): number {
  const shift = minorUnitExponent(to) - minorUnitExponent(from);
  let numerator = BigInt(cents) * rate.numerator;
  let denominator = rate.denominator;
  if (shift >= 0) numerator *= 10n ** BigInt(shift);
  else denominator *= 10n ** BigInt(-shift);
  // Half up on the magnitude: prices are never negative, and a negative would
  // round away from zero the same way, so a refund mirrors its charge.
  const negative = numerator < 0n;
  const magnitude = negative ? -numerator : numerator;
  const rounded = (2n * magnitude + denominator) / (2n * denominator);
  return Number(negative ? -rounded : rounded);
}

/** One currency's share of a total that crosses locations. */
export interface MoneyInCurrency {
  currency: string;
  cents: number;
}

/**
 * Add amounts **per currency**, never across them (QA4-D6). The order is the
 * order each currency was first met, so a clinic in one currency gets exactly
 * one entry and its readers render what they always did.
 */
export function sumByCurrency(
  amounts: readonly { currency: string; cents: number }[],
): MoneyInCurrency[] {
  const totals = new Map<string, number>();
  for (const { currency, cents } of amounts) {
    totals.set(currency, (totals.get(currency) ?? 0) + cents);
  }
  return [...totals].map(([currency, cents]) => ({ currency, cents }));
}
